import { useState, useEffect } from "react";
import {
  Drawer, Steps, Form, Input, Select, DatePicker, InputNumber, Button, Space,
  Descriptions, Alert, Switch, Row, Col, Upload, Empty, Tag, Typography,
} from "antd";
import {
  SendOutlined, LeftOutlined, RightOutlined, UploadOutlined, DeleteOutlined,
  FileTextOutlined, InboxOutlined, LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CorpClaimPortalService } from "../services/corpClaimPortalService";
import { notifyUser } from "../utils/notify";
import { DOCUMENT_TYPES } from "../constants/documentTypes";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Title } = Typography;

/**
 * Which extra field group to show is driven by the SLAMS claim-type label,
 * not a hardcoded code — SLAMS codes are opaque and change (confirmed this
 * session: the Node app's own hardcoded code map no longer matched live
 * SLAMS data). Matching on the label text is the same live-data-only
 * approach the backend takes by sourcing claim_type_code from the reference
 * sync instead of a constant.
 */
const categoryFor = (label = "") => {
  const l = label.toUpperCase();
  // Spousal/Coffin/Parent Cover are all death benefits under a different
  // SLAMS label — none of them contain the substring "DEATH", so the
  // deceased-name/date-of-death fields never rendered for a Parent Cover
  // claim (or Spousal/Coffin) even though the same fields apply to all of
  // them. Matches BUSINESS_RELEVANT_KEYWORDS' own grouping below.
  if (l.includes("DEATH") || l.includes("SPOUSAL COVER") || l.includes("COFFIN COVER") || l.includes("PARENT COVER")) {
    return "death";
  }
  if (l.includes("DISABILITY")) return "disability";
  if (l.includes("CRITICAL ILLNESS")) return "critical_illness";
  return "general";
};

/**
 * The old app only ever offered the ~8 claim types actually relevant to a
 * corporate group scheme (death, spousal/coffin/parent cover, critical
 * illness, disability, medical) out of SLAMS's full ~36-entry list, which
 * also includes things like Annuity, Cash Back and Commutation that don't
 * apply here. Its own filter used hardcoded SLAMS codes that no longer match
 * live data (confirmed this session), so this matches by label instead.
 */
const BUSINESS_RELEVANT_KEYWORDS = [
  "DEATH", "SPOUSAL COVER", "COFFIN COVER", "PARENT COVER",
  "CRITICAL ILLNESS", "PERMANENT DISABILITY", "TEMPORARY DISABILITY", "MEDICAL",
];
const isBusinessRelevant = (label = "") => {
  const l = label.toUpperCase();
  return BUSINESS_RELEVANT_KEYWORDS.some((k) => l.includes(k));
};

let uidCounter = 0;

const SubmitClaimDrawer = ({ open, onClose, onSubmitted, editClaim }) => {
  const isEdit = Boolean(editClaim);
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [claimTypes, setClaimTypes] = useState([]);
  const [claimCauses, setClaimCauses] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bankBranches, setBankBranches] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [member, setMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [category, setCategory] = useState("general");
  // Bank is tracked by its unique SLAMS code, not its display name — two
  // different banks can share the same name (confirmed live: this was
  // causing the same branch to appear multiple times when branches were
  // filtered by name instead of code).
  const [selectedBankCode, setSelectedBankCode] = useState();

  const [pendingType, setPendingType] = useState();
  const [stagedDocs, setStagedDocs] = useState([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setCurrent(0);
    setMember(null);
    setStagedDocs([]);
    setSelectedBankCode(undefined);

    (async () => {
      setLoadingLookups(true);
      try {
        const [typesRes, causesRes, banksRes, branchesRes] = await Promise.all([
          CorpClaimPortalService.referenceData("claim_type"),
          CorpClaimPortalService.referenceData("claim_cause"),
          CorpClaimPortalService.referenceData("bank_code"),
          CorpClaimPortalService.referenceData("bank_branch"),
        ]);
        const loadedTypes = (typesRes.data || []).filter((t) => isBusinessRelevant(t.label));
        const loadedCauses = causesRes.data || [];
        const loadedBanks = banksRes.data || [];
        const loadedBranches = branchesRes.data || [];
        setClaimTypes(loadedTypes);
        setClaimCauses(loadedCauses);
        setBanks(loadedBanks);
        setBankBranches(loadedBranches);

        // Editing: prefill from the claim's own stored values rather than a
        // fresh SLAMS lookup — bank/branch need the lists above loaded first
        // to resolve the stored names back to the Select controls' codes.
        if (editClaim) {
          const t = loadedTypes.find((ct) => ct.code === editClaim.claim_type_code);
          setCategory(categoryFor(t?.label));

          const bank = editClaim.bank_name ? loadedBanks.find((b) => b.label === editClaim.bank_name) : null;
          const branch = bank && editClaim.branch
            ? loadedBranches.find((b) => b.code.startsWith(`${bank.code}:`) && b.label.endsWith(` — ${editClaim.branch}`))
            : null;
          if (bank) setSelectedBankCode(bank.code);

          const toDayjs = (d) => (d ? dayjs(d) : undefined);
          form.setFieldsValue({
            ...editClaim,
            claimant_date_of_birth: toDayjs(editClaim.claimant_date_of_birth),
            deceased_date_of_birth: toDayjs(editClaim.deceased_date_of_birth),
            deceased_date_of_death: toDayjs(editClaim.deceased_date_of_death),
            burial_date: toDayjs(editClaim.burial_date),
            date_of_injury: toDayjs(editClaim.date_of_injury),
            payment_start_date: toDayjs(editClaim.payment_start_date),
            payment_end_date: toDayjs(editClaim.payment_end_date),
            diagnosis_date: toDayjs(editClaim.diagnosis_date),
            event_date: toDayjs(editClaim.event_date),
            bank_code: bank?.code,
            branch_code: branch?.code,
          });
        }
      } catch {
        notifyUser("error", "Could not load claim types and bank lists. Please reopen the form.", "Error");
      } finally {
        setLoadingLookups(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const steps = isEdit
    ? [{ title: "Scheme & Claim Type" }, { title: "Claimant" }, { title: "Claim Details" }, { title: "Review & Save" }]
    : [{ title: "Scheme & Claim Type" }, { title: "Claimant" }, { title: "Claim Details" }, { title: "Documents" }, { title: "Review & Submit" }];
  const reviewStep = steps.length - 1;
  const documentsStep = isEdit ? null : 3;

  const stepFields = [
    ["scheme_no", "member_staff_no", "claim_type_code"],
    ["claimant_name", "claimant_mobile", "claimant_address", "claimant_date_of_birth"],
    // SLAMS's createClaims rejects a submission with no event_date or
    // claim_cause — confirmed live, this was silently stranding approved
    // claims in the retry queue.
    ["event_date", "claim_cause"],
    [],
  ];

  /**
   * Claimant name, date of birth, mobile, address and bank details all come
   * straight off the verified SLAMS record — matching the old app's own
   * "pre-fills claimant/banking fields before Step 2 renders" behaviour —
   * rather than asking the requester to retype what SLAMS already knows.
   * Bank/branch come back from SLAMS as codes, so they're resolved against
   * the already-loaded bank/branch reference lists to get the same option
   * labels the Select controls use.
   */
  const applyMemberDetails = (details) => {
    const basic = details.basic_information || {};
    const contact = details.contact_information || {};
    const banking = details.banking_details || {};

    const values = { claimant_name: basic.full_name || undefined };

    if (basic.date_of_birth) {
      const dob = dayjs(basic.date_of_birth);
      if (dob.isValid()) values.claimant_date_of_birth = dob;
    }
    if (contact.mobile_number) values.claimant_mobile = contact.mobile_number;
    if (contact.address) values.claimant_address = contact.address;

    if (banking.bank_code && banks.some((b) => b.code === banking.bank_code)) {
      values.bank_code = banking.bank_code;
      setSelectedBankCode(banking.bank_code);

      if (banking.bank_branch_id) {
        const branchCode = `${banking.bank_code}:${banking.bank_branch_id}`;
        if (bankBranches.some((b) => b.code === branchCode)) {
          values.branch_code = branchCode;
        }
      }
    }
    if (banking.bank_account) values.account_number = banking.bank_account;

    form.setFieldsValue(values);
  };

  /**
   * SLAMS verification is not a separate button here — it runs automatically
   * the moment the requester tries to leave Step 1, exactly like the
   * original app (fetchMemberDetails on Step-1 "Continue", not on blur). A
   * member that isn't found, or whose scheme isn't active, blocks
   * progressing rather than letting an unverifiable claim through.
   */
  const verifyMember = async () => {
    const schemeNo = form.getFieldValue("scheme_no");
    const staffNo = form.getFieldValue("member_staff_no");

    setVerifying(true);
    try {
      const res = await CorpClaimPortalService.memberLookup({ scheme_no: schemeNo, staff_no: staffNo });
      const details = res.data?.member_details;
      if (!details) {
        notifyUser("error", "No SLAMS member found for that scheme and staff number.", "Not found");
        setMember(null);
        return false;
      }
      if (details.scheme_information?.scheme_is_active === false) {
        setMember(details);
        notifyUser("error", "This scheme is not currently active with SLAMS, so a claim cannot be raised against it.", "Scheme inactive");
        return false;
      }
      setMember(details);
      applyMemberDetails(details);
      notifyUser("success", `Verified ${details.basic_information?.full_name || "member"} with SLAMS.`, "Verified");
      return true;
    } catch (e) {
      notifyUser("error", e?.response?.data?.message || "Could not verify this member with SLAMS.", "Error");
      setMember(null);
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const next = async () => {
    try {
      if (current < stepFields.length) await form.validateFields(stepFields[current]);
    } catch {
      return;
    }
    if (current === 0) {
      const ok = await verifyMember();
      if (!ok) return;
    }
    setCurrent((c) => c + 1);
  };
  const prev = () => setCurrent((c) => c - 1);

  const selectedBankLabel = banks.find((b) => b.code === selectedBankCode)?.label;

  // Filtered by the bank's unique code, not its display name — two banks
  // can share a name (confirmed live), which was making the same branch
  // show up once per bank that happened to share the selected name.
  const branchOptions = selectedBankCode
    ? bankBranches
      .filter((b) => b.code.startsWith(`${selectedBankCode}:`))
      .map((b) => ({
        value: b.code,
        label: selectedBankLabel && b.label.startsWith(`${selectedBankLabel} — `)
          ? b.label.slice(selectedBankLabel.length + 3)
          : b.label,
      }))
    : [];

  const handleAddDocument = (file) => {
    if (!pendingType) {
      notifyUser("error", "Choose a document type first.", "Missing details");
      return false;
    }
    setStagedDocs((docs) => [...docs, {
      uid: ++uidCounter,
      file,
      name: file.name,
      type: pendingType,
      typeLabel: DOCUMENT_TYPES.find((t) => t.value === pendingType)?.label,
    }]);
    setPendingType(undefined);
    return false;
  };
  const removeDocument = (uid) => setStagedDocs((docs) => docs.filter((d) => d.uid !== uid));

  /** bank_code/branch_code exist only for the Select controls (codes are
   *  the unique key) — the backend column is a plain name string, resolved
   *  from the reference lists just before it leaves the component. */
  const resolveBankFields = (values) => {
    const branchCode = values.branch_code;
    const branchEntry = branchCode ? bankBranches.find((b) => b.code === branchCode) : null;
    return {
      bank_name: banks.find((b) => b.code === values.bank_code)?.label,
      branch: branchEntry && selectedBankLabel && branchEntry.label.startsWith(`${selectedBankLabel} — `)
        ? branchEntry.label.slice(selectedBankLabel.length + 3)
        : branchEntry?.label,
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = form.getFieldsValue(true);
      const { bank_code: _bankCode, branch_code: _branchCode, ...rest } = values;
      const payload = {
        ...rest,
        ...resolveBankFields(values),
        claimant_date_of_birth: values.claimant_date_of_birth?.format("YYYY-MM-DD"),
        deceased_date_of_birth: values.deceased_date_of_birth?.format("YYYY-MM-DD"),
        deceased_date_of_death: values.deceased_date_of_death?.format("YYYY-MM-DD"),
        burial_date: values.burial_date?.format("YYYY-MM-DD"),
        date_of_injury: values.date_of_injury?.format("YYYY-MM-DD"),
        payment_start_date: values.payment_start_date?.format("YYYY-MM-DD"),
        payment_end_date: values.payment_end_date?.format("YYYY-MM-DD"),
        diagnosis_date: values.diagnosis_date?.format("YYYY-MM-DD"),
        event_date: values.event_date?.format("YYYY-MM-DD"),
      };

      if (isEdit) {
        await CorpClaimPortalService.update(editClaim.id, payload);
        notifyUser("success", "Your claim has been updated.", "Saved");
        onSubmitted?.(editClaim.id);
        return;
      }

      const res = await CorpClaimPortalService.submit(payload);
      const claimId = res.data.id;

      let failedUploads = 0;
      for (const doc of stagedDocs) {
        try {
          const formData = new FormData();
          formData.append("file", doc.file);
          formData.append("name", doc.name);
          formData.append("type", doc.type);
          await CorpClaimPortalService.uploadDocument(claimId, formData);
        } catch {
          failedUploads += 1;
        }
      }

      if (failedUploads > 0) {
        notifyUser(
          "error",
          `Your claim was submitted, but ${failedUploads} document${failedUploads === 1 ? "" : "s"} failed to upload. You can add ${failedUploads === 1 ? "it" : "them"} from the claim page.`,
          "Submitted with a warning",
        );
      } else {
        notifyUser("success", "Your claim has been submitted for review.", "Submitted");
      }
      onSubmitted?.(claimId);
    } catch (e) {
      notifyUser("error", e?.response?.data?.message || "Could not save this claim.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const values = form.getFieldsValue(true);
  const resolvedBank = resolveBankFields(values);

  const footer = (
    <Space style={{ width: "100%", justifyContent: "space-between" }}>
      <Button icon={<LeftOutlined />} disabled={current === 0} onClick={prev}>Back</Button>
      {current < steps.length - 1 ? (
        <Button type="primary" loading={verifying} onClick={next}>
          {verifying ? "Verifying with SLAMS…" : <>Continue <RightOutlined /></>}
        </Button>
      ) : (
        <Button type="primary" icon={submitting ? <LoadingOutlined /> : <SendOutlined />} loading={submitting} onClick={handleSubmit}>
          {submitting ? "Saving…" : isEdit ? "Save Changes" : "Submit Claim"}
        </Button>
      )}
    </Space>
  );

  return (
    <Drawer title={isEdit ? "Edit Claim" : "Submit a Claim"} open={open} onClose={onClose} width="85%" destroyOnClose footer={footer}>
      <div className="container">
      <Steps current={current} items={steps} style={{ marginBottom: 32 }} />

      <Form form={form} layout="vertical" size="large" requiredMark="optional" onValuesChange={(changed) => {
        if (changed.claim_type_code !== undefined) {
          const t = claimTypes.find((c) => c.code === changed.claim_type_code);
          setCategory(categoryFor(t?.label));
        }
        if (changed.bank_code !== undefined) {
          setSelectedBankCode(changed.bank_code);
          form.setFieldsValue({ branch_code: undefined });
        }
      }}>
        <div style={{ display: current === 0 ? "block" : "none" }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="scheme_no" label="Scheme number" rules={[{ required: true, message: "Scheme number is required" }]}>
                <Input placeholder="e.g. GLA/2018/00706" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="member_staff_no"
                label="Member staff number"
                rules={[{ required: true, message: "Staff number is required to verify the member" }]}
                extra="Used to look up and verify the member with SLAMS."
              >
                <Input placeholder="e.g. 00027" />
              </Form.Item>
            </Col>
          </Row>

          {member && (
            <Alert
              type={member.scheme_information?.scheme_is_active ? "success" : "error"}
              showIcon
              style={{ marginBottom: 16 }}
              message={member.basic_information?.full_name}
              description={
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Scheme">{member.scheme_information?.scheme_name}</Descriptions.Item>
                  <Descriptions.Item label="Scheme active">{member.scheme_information?.scheme_is_active ? "Yes" : "No"}</Descriptions.Item>
                  <Descriptions.Item label="Sum assured">{member.financial_information?.total_sum_assured?.toLocaleString?.() ?? "—"}</Descriptions.Item>
                </Descriptions>
              }
            />
          )}

          <Form.Item name="claim_type_code" label="Claim type" rules={[{ required: true, message: "Select a claim type" }]}>
            <Select
              showSearch
              loading={loadingLookups}
              placeholder="Select claim type"
              optionFilterProp="label"
              options={claimTypes.map((t) => ({ value: t.code, label: t.label }))}
            />
          </Form.Item>
        </div>

        <div style={{ display: current === 1 ? "block" : "none" }}>
          <Form.Item
            name="claimant_name"
            label="Claimant name"
            rules={[{ required: true, message: "Claimant name is required" }]}
            extra="Filled in automatically from the verified SLAMS member."
          >
            <Input placeholder="Full name of the person claiming" readOnly />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="claimant_mobile" label="Claimant mobile">
                <Input placeholder="e.g. 024 412 3456" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="claimant_date_of_birth" label="Claimant date of birth">
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="claimant_address" label="Claimant address">
            <TextArea rows={2} maxLength={2000} />
          </Form.Item>

          <Title level={5} style={{ marginTop: 8 }}>Payment details</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="bank_account_name" label="Bank account name"><Input /></Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="bank_code" label="Bank">
                <Select
                  showSearch
                  loading={loadingLookups}
                  placeholder="Select bank"
                  optionFilterProp="label"
                  options={banks.map((b) => ({ value: b.code, label: b.label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="branch_code" label="Branch">
                <Select
                  showSearch
                  disabled={!selectedBankCode}
                  placeholder={selectedBankCode ? "Select branch" : "Select a bank first"}
                  optionFilterProp="label"
                  options={branchOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="account_number" label="Account number"><Input /></Form.Item>
            </Col>
          </Row>
        </div>

        <div style={{ display: current === 2 ? "block" : "none" }}>
          {category === "death" && (
            <>
              <Title level={5}>Deceased details</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item name="deceased_name" label="Deceased name"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="relationship_to_deceased" label="Relationship to deceased"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="deceased_date_of_birth" label="Deceased date of birth"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="deceased_date_of_death" label="Date of death"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="place_of_death" label="Place of death"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="cause_of_death" label="Cause of death"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="funeral_home_name" label="Funeral home"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="burial_date" label="Burial date"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="burial_location" label="Burial location"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="burial_costs" label="Burial costs"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
              </Row>
              <Form.Item name="burial_details" label="Burial details"><TextArea rows={2} /></Form.Item>
            </>
          )}

          {category === "disability" && (
            <>
              <Title level={5}>Disability details</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="disability_type" label="Disability type">
                    <Select
                      allowClear
                      placeholder="Select disability type"
                      options={[
                        { value: "PTD", label: "Permanent Total Disability (PTD)" },
                        { value: "TTD", label: "Temporary Total Disability (TTD)" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}><Form.Item name="medical_provider" label="Medical provider"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="date_of_injury" label="Date of injury"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="estimated_payment_duration_months" label="Estimated duration (months)"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="monthly_payment_amount" label="Monthly payment amount"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
                <Col xs={24} md={12}>
                  <Form.Item name="payment_frequency" label="Payment frequency">
                    <Select
                      allowClear
                      placeholder="Select payment frequency"
                      options={[
                        { value: "WEEKLY", label: "Weekly" },
                        { value: "BI_WEEKLY", label: "Bi-Weekly" },
                        { value: "MONTHLY", label: "Monthly" },
                        { value: "QUARTERLY", label: "Quarterly" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}><Form.Item name="payment_start_date" label="Payment start date"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="payment_end_date" label="Payment end date"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
              </Row>
            </>
          )}

          {category === "critical_illness" && (
            <>
              <Title level={5}>Critical illness details</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item name="diagnosis" label="Diagnosis"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="diagnosis_date" label="Diagnosis date"><DatePicker style={{ width: "100%" }} format="DD MMM YYYY" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="illness_stage" label="Illness stage"><Input /></Form.Item></Col>
                <Col xs={24} md={12}>
                  <Form.Item name="condition_severity_stage" label="Condition severity">
                    <Select
                      allowClear
                      placeholder="Select condition severity"
                      options={[
                        { value: "EARLY", label: "Early" },
                        { value: "INTERMEDIATE", label: "Intermediate" },
                        { value: "ADVANCED", label: "Advanced" },
                        { value: "TERMINAL", label: "Terminal" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}><Form.Item name="payout_percentage" label="Payout percentage"><InputNumber style={{ width: "100%" }} min={0} max={100} /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="treatment_required" label="Treatment required" valuePropName="checked"><Switch /></Form.Item></Col>
                <Col xs={12} md={6}><Form.Item name="ongoing_treatment" label="Ongoing treatment" valuePropName="checked"><Switch /></Form.Item></Col>
              </Row>
            </>
          )}

          <Title level={5}>Cause &amp; event</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="claim_cause" label="Claim cause" rules={[{ required: true, message: "Claim cause is required" }]}>
                <Select
                  showSearch
                  loading={loadingLookups}
                  placeholder="Select claim cause"
                  optionFilterProp="label"
                  options={claimCauses.map((c) => ({ value: c.code, label: c.label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="event_date" label="Event date" rules={[{ required: true, message: "SLAMS requires the date of the incident/event" }]}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>Benefit</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}><Form.Item name="benefit_amount" label="Benefit amount claimed"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
          </Row>
        </div>

        <div style={{ display: !isEdit && current === documentsStep ? "block" : "none" }}>
          <Title level={5}>Attach supporting documents</Title>
          <Space wrap style={{ marginBottom: 14 }}>
            <Select
              placeholder="Document type"
              value={pendingType}
              onChange={setPendingType}
              style={{ width: 240 }}
              options={DOCUMENT_TYPES}
            />
          </Space>

          <Dragger
            beforeUpload={handleAddDocument}
            showUploadList={false}
            disabled={!pendingType}
            style={{ marginBottom: 20 }}
          >
            <p style={{ fontSize: 26, color: "#189F6B", margin: "6px 0 4px" }}><UploadOutlined /></p>
            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
              {pendingType ? "Click or drag a file here to attach" : "Choose a document type above first"}
            </p>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
              Attached now, uploaded automatically once the claim is submitted
            </p>
          </Dragger>

          {stagedDocs.length ? (
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              {stagedDocs.map((d) => (
                <div
                  key={d.uid}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg-hover)",
                  }}
                >
                  <FileTextOutlined style={{ color: "#189F6B" }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5 }}>
                    {d.name}
                  </span>
                  <Tag color="green">{d.typeLabel}</Tag>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeDocument(d.uid)} />
                </div>
              ))}
            </Space>
          ) : (
            <Empty
              image={<InboxOutlined style={{ fontSize: 32, color: "var(--text-secondary)" }} />}
              description="No documents attached yet — you can also add these after submitting."
              style={{ padding: "12px 0" }}
            />
          )}
        </div>

        {current === reviewStep && (
          <div>
            {!isEdit && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${stagedDocs.length} document${stagedDocs.length === 1 ? "" : "s"} will be uploaded once you submit.`}
              />
            )}
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Scheme number">{values.scheme_no}</Descriptions.Item>
              <Descriptions.Item label="Staff number">{values.member_staff_no || "—"}</Descriptions.Item>
              <Descriptions.Item label="Claim type">{claimTypes.find((t) => t.code === values.claim_type_code)?.label || values.claim_type_code}</Descriptions.Item>
              <Descriptions.Item label="Claimant">{values.claimant_name}</Descriptions.Item>
              <Descriptions.Item label="Claimant mobile">{values.claimant_mobile || "—"}</Descriptions.Item>
              <Descriptions.Item label="Bank">{resolvedBank.bank_name ? `${resolvedBank.bank_name} (${resolvedBank.branch || "—"}) — ${values.account_number || "—"}` : "—"}</Descriptions.Item>
              {values.deceased_name && <Descriptions.Item label="Deceased">{values.deceased_name}</Descriptions.Item>}
              {values.benefit_amount != null && <Descriptions.Item label="Benefit claimed">{values.benefit_amount}</Descriptions.Item>}
              {!isEdit && <Descriptions.Item label="Documents">{stagedDocs.length}</Descriptions.Item>}
            </Descriptions>
          </div>
        )}
      </Form>
      </div>
    </Drawer>
  );
};

export default SubmitClaimDrawer;
